import { motion } from "framer-motion";
import { TrendingUp, Anchor, Scale, Truck } from "lucide-react";

const ITEMS = [
    {
        icon: TrendingUp,
        title: "Higher Cost of Capital",
        body: "Lenders are pricing carbon into loan books. High-emission borrowers face wider spreads, tighter covenants, and provisioning drag.",
        stat: "+120–260 bps",
        statLabel: "Emission-linked spread"
    },
    {
        icon: Anchor,
        title: "CBAM Export Penalties",
        body: "The EU's Carbon Border Adjustment Mechanism converts unmeasured emissions into a per-tonne charge from 2026. Unpriced exposure becomes lost margin.",
        stat: "€85 / tCO₂e",
        statLabel: "Projected 2026 CBAM"
    },
    {
        icon: Scale,
        title: "Regulatory Non-Compliance",
        body: "BRSR Core, RBI supervisory guidance, and ISSB-aligned disclosures create escalating enforcement risk. Reactive posture is no longer viable.",
        stat: "1,000+",
        statLabel: "BRSR-mandated entities"
    },
    {
        icon: Truck,
        title: "Supply-Chain Disruption",
        body: "Global buyers are rewriting supplier codes with Scope 3 thresholds. Non-compliant vendors are silently removed from approved lists.",
        stat: "68%",
        statLabel: "Buyers with carbon KPIs"
    }
];

export default function WhatWeSolve() {
    return (
        <section className="relative py-24 lg:py-32 border-t border-navy/10" data-testid="whatwesolve-section">
            <div className="max-w-[1500px] mx-auto px-6 lg:px-10">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 mb-20">
                    <div className="lg:col-span-4">
                        <div className="flex items-center gap-4 mb-6">
                            <div className="w-10 h-px bg-gold"></div>
                            <span className="font-mono text-[11px] tracking-[0.28em] uppercase text-gold">What We Solve</span>
                        </div>
                        <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.02em]">
                            Climate risk is <em className="text-gold not-italic font-light">no longer</em> theoretical.
                        </h2>
                    </div>
                    <div className="lg:col-span-7 lg:col-start-6 lg:self-end">
                        <p className="text-navy/60 text-lg leading-relaxed">
                            It shows up on balance sheets as widening credit spreads, lost export contracts, regulatory penalties, and vendor delistings. Carbonis makes these risks visible, quantifiable, and manageable — before they compound.
                        </p>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-navy/10">
                    {ITEMS.map((it, i) => {
                        const Icon = it.icon;
                        return (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 30 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, margin: "-80px" }}
                                transition={{ duration: 0.7, delay: i * 0.08 }}
                                className="group bg-paper p-8 lg:p-12 flex flex-col gap-6 min-h-[320px] hover:bg-white transition-colors duration-500"
                                data-testid={`solve-item-${i}`}
                            >
                                <div className="flex items-start justify-between">
                                    <div className="w-12 h-12 border border-navy/15 flex items-center justify-center group-hover:border-gold transition-colors duration-500">
                                        <Icon size={20} strokeWidth={1.4} className="text-gold" />
                                    </div>
                                    <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-navy/40">0{i + 1} / 04</span>
                                </div>
                                <div>
                                    <h3 className="font-serif text-3xl leading-tight tracking-tight text-navy mb-4">{it.title}</h3>
                                    <p className="text-navy/60 text-[15px] leading-relaxed max-w-[420px]">{it.body}</p>
                                </div>
                                <div className="mt-auto pt-6 border-t border-navy/10 flex items-baseline justify-between">
                                    <span className="font-serif text-2xl text-gold">{it.stat}</span>
                                    <span className="font-mono text-[10px] tracking-[0.22em] uppercase text-navy/40">{it.statLabel}</span>
                                </div>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
